import { useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import type { UpgradeAsk } from '../context/UpgradeContext'
import { planFor, PLANS, PRICES_URL, type Plan } from '../plans'
import { requestRenewal, renewalStatus } from '../services/renewal'
import { FEATURES } from '../types'
import { ExternalLinkIcon, LockIcon, XIcon } from './icons'
import './pos/ConfirmDialog.css'
import './UpgradeDialog.css'

/**
 * Kilitli özelliğe ya da dolan personel sınırına basınca (UpgradeContext).
 * Hangi pakette olduğunu söyler; patron yükseltme talebi gönderebilir
 * (renewal fonksiyonu, info@'ya posta). Ödeme yok, talep elle karşılanıyor.
 */
export function UpgradeDialog({ what, onClose }: { what: UpgradeAsk; onClose: () => void }) {
  const { tenantId, plan, staffLimit, isOwner } = useAuth()
  const idx = plan ? PLANS.findIndex((p) => p.id === plan.id) : -1
  // İstenen paket: özelliği açan paket, personelde bir üstü.
  const want: Plan | null = what === 'staff' ? (PLANS[idx + 1] ?? null) : planFor(what)
  const spec = what === 'staff' ? null : FEATURES.find((f) => f.key === what)
  const title = what === 'staff' ? 'Personel sınırı doldu' : (spec?.name ?? 'Bu özellik')

  /** Bekleyen talebin tarihi; undefined bilinmiyor. */
  const [pending, setPending] = useState<string | null | undefined>(undefined)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!tenantId || !isOwner) return
    let alive = true
    renewalStatus(tenantId)
      .then((d) => alive && setPending(d))
      .catch(() => alive && setPending(null))
    return () => {
      alive = false
    }
  }, [tenantId, isOwner])

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [onClose])

  const send = async () => {
    if (!tenantId || !want) return
    setSending(true)
    setError(null)
    try {
      setPending(await requestRenewal(tenantId, want.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gönderilemedi.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true" aria-labelledby="upg-title" onClick={onClose}>
      <div className="dlg__card dlg__card--x upg" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="dlg__x" onClick={onClose} aria-label="Kapat">
          <XIcon />
        </button>
        <span className="upg__icon" aria-hidden="true">
          <LockIcon />
        </span>
        <h2 id="upg-title">{title}</h2>

        <div className="upg__box">
          {what === 'staff' ? (
            <p>
              {plan?.name} paketinde en fazla <b>{staffLimit}</b> kasa ve garson var.
              {want ? <> Daha fazlası için <b>{want.name}</b> paketine geçin.</> : null}
            </p>
          ) : want ? (
            <p>
              {spec?.description} <b>{want.name}</b> paketinde var
              {plan ? <>, siz şu an <b>{plan.name}</b> paketindesiniz.</> : '.'}
            </p>
          ) : (
            <p>{spec?.description} Ek modül olarak açılır; bizimle iletişime geçin.</p>
          )}
        </div>

        {isOwner && pending && (
          <p className="upg__sent" role="status">
            Talebiniz alındı, en kısa sürede size ulaşacağız.
          </p>
        )}
        {!isOwner && <p className="upg__hint">Paketi işletme sahibi değiştirebilir.</p>}
        {error && <p className="upg__err">{error}</p>}

        <div className="dlg__buttons dlg__buttons--even">
          <a className="btn btn--ghost" href={PRICES_URL} target="_blank" rel="noopener">
            Paketleri gör <ExternalLinkIcon />
          </a>
          {isOwner && want && !pending && (
            <button
              type="button"
              className="btn btn--primary"
              disabled={sending || pending === undefined}
              onClick={() => void send()}
            >
              {sending ? 'Gönderiliyor…' : `${want.name} paketine geç`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
